import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import '../landing.css'

export function Landing() {
  const navigate = useNavigate()
  const { user } = useAuth()

  return (
    <div className="bg-surface font-body text-on-surface min-h-screen">
      {/* TopNavBar */}
        <nav className="fixed top-0 w-full z-50 glass-nav shadow-sm">
          <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">
            <div className="flex items-center gap-8">
              <span className="text-xl font-bold tracking-tighter text-blue-900">FeedbackKit</span>
              <div className="hidden md:flex gap-6 items-center">
                <a className="font-manrope text-sm font-medium tracking-tight text-slate-600 hover:text-blue-700 transition-colors" href="#features">Features</a>
                <a className="font-manrope text-sm font-medium tracking-tight text-slate-600 hover:text-blue-700 transition-colors" href="#pricing">Pricing</a>
                <a className="font-manrope text-sm font-medium tracking-tight text-slate-600 hover:text-blue-700 transition-colors" href="#">Docs</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <button
                  onClick={() => navigate('/dashboard/overview')}
                  className="bg-primary text-on-primary font-manrope text-sm font-semibold px-5 py-2 rounded-lg scale-95 active:opacity-80 transition-all shadow-sm"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="font-manrope text-sm font-medium text-slate-600 hover:text-blue-700 transition-colors px-4 py-2"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="bg-primary text-on-primary font-manrope text-sm font-semibold px-5 py-2 rounded-lg scale-95 active:opacity-80 transition-all shadow-sm"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>

        <main className="pt-24">
          {/* Hero Section */}
          <section className="max-w-7xl mx-auto px-6 py-20 lg:py-32 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-fixed text-on-secondary-fixed mb-6">
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>rocket_launch</span>
                <span className="font-label text-[0.7rem] uppercase tracking-widest font-bold">Now supporting Expo SDK 50</span>
              </div>
              <h1 className="font-headline text-5xl lg:text-7xl font-extrabold tracking-tighter text-on-surface leading-[1.1] mb-6">
                Plug-and-play mobile feedback for <span className="text-secondary">indie developers.</span>
              </h1>
              <p className="text-lg lg:text-xl text-on-surface-variant max-w-xl mb-10 leading-relaxed">
                Add a fully functional feedback form to your React Native or Expo app in minutes. No backend required. Capture screenshots and device logs automatically.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/register')}
                  className="hero-gradient text-on-primary font-headline font-bold px-8 py-4 rounded-lg flex items-center justify-center gap-2 group transition-all"
                >
                  Get Started Free
                  <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </button>
                <button className="bg-surface-container-high text-primary font-headline font-bold px-8 py-4 rounded-lg flex items-center justify-center gap-2 hover:bg-surface-container-highest transition-colors">
                  <span className="material-symbols-outlined">play_circle</span>
                  Watch Setup
                </button>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-2">
                  <img className="w-8 h-8 rounded-full border-2 border-surface" alt="portrait of a young smiling tech entrepreneur" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDtr-_aShjCi7RL1pN_uIAa1qCJh8RYx3Out0YMyAyZl7WoVqJh9qBnj7j9Z3XDyO_WDdNk0cv89VmUibynKBrR3Uy4Cgkq6NxKvxAmo_7a4zJg5ERIi4CRLVkkFCVsQlE4xpKyp7EII-DDTkiS6yXJt4aCYf-YOCHM8d2jlm__HNhmGha1hsOT_clrcI9k0_TrIr98IBbn8W1RAoGoQ6RW9K44HLKq8LKfM5pNf493q5uVc9Q51Izjk9wTUlpBO_tDV724Q7hg0Pg" />
                  <img className="w-8 h-8 rounded-full border-2 border-surface" alt="profile photo of a female software engineer outdoors" src="https://lh3.googleusercontent.com/aida-public/AB6AXuCYHZPCP6zTHc8bRoFxCM6cAkdAx8A-fzh0IR5gUupYA8tbZbvFdZOwB5XXM8yG5UeGaLOuabXzcrciGe7UNi0iJFr20CUWLFcwVDSugsQ2F6lKX1dOAUdQ4lwW54D-b9EGuISTnPsFNePhLdPEDiMlXZ0gDNlU1H6PC1wgqQkj442ZFv66gVwZdzoRmi7ZQPOf-b1_gA29WtW-10Uw9C-iYEImslb2H66IVgA3ACJPMX6SCw93PKrS7cQr7PLHIZfBksOMOlDi8vo" />
                  <img className="w-8 h-8 rounded-full border-2 border-surface" alt="casual portrait of a male developer wearing glasses" src="https://lh3.googleusercontent.com/aida-public/AB6AXuAYVzzm_JpZojxxcyHVrZkCn2Ggnm5ZdxFBwPpN3fuSY39WOX8h3Ld_Zhdy_RaS6CE9GOcPDoHxZg4bJcSpEzowiufxAJ5itzOS2d5nLRDJD6H4P5aI9qSRvBTVaBYBzBDtxOglEBHRvFLZLr0BUXeT9HZlzOwNnu4zkcOeUX6DaIvIJ7tyTx0cyq73ubk_tXMXTzFDKdTWap7UMjGgiyWQjC_UvTgp8Mjhtuam-rCalXiOEhc1IbFzXPpX6eJHh1P9NkG6LX4F0Ro" />
                </div>
                <p className="text-sm text-on-surface-variant font-medium">Joined by 340+ members on Discord</p>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute -inset-4 bg-secondary/5 rounded-3xl blur-2xl group-hover:bg-secondary/10 transition-colors"></div>
              <div className="relative bg-surface-container-lowest rounded-2xl p-4 card-shadow border border-outline-variant/20">
                <img className="rounded-xl w-full h-auto" alt="mobile app interface showing a feedback submission form" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDeCJbrLWF6KK5hSapbzMXMhLzNfZTxKApJDiLwBsJiqTCtCprBToJp2JnheFKASddn9ZQ064VayjRfTZPrWI2pR7TA0kx2pxDasNNz5SOD20F2cEE6-ta9AUqEaA3uFUSCONrELNq3flkFW9GCd6aCvFNb90A-EfDUYXLqZ5kOnUHLmW6UPU56wvN8J_0f963mDimnTye9yU9CIjkGJFyysIEDFoVepm75Z4ZTBn8x7JrhPEzZpV7iQKE0baGQJ6uuZa58-E9ApWA" />
                <div className="absolute -bottom-6 -left-6 bg-surface-container-highest p-6 rounded-xl shadow-xl max-w-xs border border-outline-variant/30 hidden md:block">
                  <p className="font-label text-[0.65rem] uppercase tracking-widest text-secondary font-bold mb-2">Live Metadata</p>
                  <div className="space-y-2">
                    <div className="h-2 w-full bg-outline-variant/20 rounded"></div>
                    <div className="h-2 w-3/4 bg-outline-variant/20 rounded"></div>
                    <div className="flex gap-2 pt-2">
                      <span className="px-2 py-1 bg-secondary-fixed text-on-secondary-fixed text-[10px] rounded">iOS 17.2</span>
                      <span className="px-2 py-1 bg-secondary-fixed text-on-secondary-fixed text-[10px] rounded">iPhone 15</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="bg-surface-container-low py-24">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="font-headline text-3xl lg:text-5xl font-extrabold tracking-tight text-on-surface mb-4">Go from zero to feedback in 2 minutes.</h2>
                <p className="text-on-surface-variant max-w-2xl mx-auto">Skip the backend infrastructure. Our SDK handles the collection, storage, and notification pipeline for you.</p>
              </div>
              <div className="grid md:grid-cols-4 gap-8">
                <div className="relative">
                  <div className="mb-6 flex items-center justify-center w-12 h-12 rounded-lg bg-primary text-on-primary font-bold text-xl">1</div>
                  <h3 className="font-headline font-bold text-lg mb-2">Install SDK</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">Simply run <code className="bg-surface-container-highest px-1.5 py-0.5 rounded font-mono text-primary">npm install @feedbackkit/sdk</code> in your project folder.</p>
                </div>
                <div className="relative">
                  <div className="mb-6 flex items-center justify-center w-12 h-12 rounded-lg bg-primary text-on-primary font-bold text-xl">2</div>
                  <h3 className="font-headline font-bold text-lg mb-2">Add Component</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">Wrap your app or specific screens with our lightweight <code className="bg-surface-container-highest px-1.5 py-0.5 rounded font-mono text-primary">&lt;FeedbackProvider /&gt;</code>.</p>
                </div>
                <div className="relative">
                  <div className="mb-6 flex items-center justify-center w-12 h-12 rounded-lg bg-primary text-on-primary font-bold text-xl">3</div>
                  <h3 className="font-headline font-bold text-lg mb-2">Trigger UI</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">Trigger the UI via a button, shake gesture, or floating bubble with one line of code.</p>
                </div>
                <div className="relative">
                  <div className="mb-6 flex items-center justify-center w-12 h-12 rounded-lg bg-primary text-on-primary font-bold text-xl">4</div>
                  <h3 className="font-headline font-bold text-lg mb-2">See Results</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">View structured feedback, user metadata, and screenshots instantly in your dashboard.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-24 max-w-7xl mx-auto px-6" id="features">
            <div className="text-center mb-16">
              <span className="font-label text-[0.7rem] uppercase tracking-widest text-secondary font-black mb-4 block">Product Features</span>
              <h2 className="font-headline text-3xl lg:text-5xl font-extrabold tracking-tight text-on-surface">Everything you need to ship better.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Large Feature */}
              <div className="md:col-span-2 bg-surface-container-lowest p-8 rounded-2xl border border-outline-variant/20 card-shadow flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary mb-6">
                    <span className="material-symbols-outlined">dashboard_customize</span>
                  </div>
                  <h3 className="font-headline text-2xl font-bold mb-4">Dashboard analytics &amp; trends</h3>
                  <p className="text-on-surface-variant mb-8 max-w-md">Visualize user sentiment over time. Spot recurring issues with automated tagging and priority heatmaps that show exactly where your app needs work.</p>
                </div>
                <div className="rounded-xl overflow-hidden border border-outline-variant/30">
                  <img className="w-full object-cover h-48" alt="data dashboard showing bar charts and line graphs" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBRGKkd0uR9NNQq21Tm0OnXnUZUfFyTEwl8-11x-yj8KU7OKlt9rqrvTeWTPLLCjaNPZhafwjt1dainYBSZmr1TsKylCl0JGs_-cLbmI8hDjM6OpvSM82QUdQwhjn6uM31eD6GMw1C7dh-oHbT10PV58RQI_u5x2BaFWM7N8t_wt8dK9hN86WANn1bcxM6uqnPX57YhOEcATR4XiP5140ARcSYBgRk2BMTAD3GFMJREu3clbc1MCRri77o1FSTJtnHXJG5m9oV4oDw" />
                </div>
              </div>
              {/* Small Feature */}
              <div className="bg-primary text-on-primary p-8 rounded-2xl flex flex-col justify-between overflow-hidden relative">
                <div className="relative z-10">
                  <h3 className="font-headline text-2xl font-bold mb-4">Auto-detected Metadata</h3>
                  <p className="text-primary-container text-sm">No more asking "which version are you on?". We capture OS, model, battery, and network state automatically.</p>
                </div>
                <div className="mt-8 relative z-10">
                  <div className="space-y-2 opacity-80">
                    <div className="flex justify-between text-xs font-mono border-b border-white/10 pb-1"><span>OS</span><span>Android 14</span></div>
                    <div className="flex justify-between text-xs font-mono border-b border-white/10 pb-1"><span>DEVICE</span><span>Pixel 8 Pro</span></div>
                    <div className="flex justify-between text-xs font-mono border-b border-white/10 pb-1"><span>APP_VER</span><span>v2.4.1-rc</span></div>
                  </div>
                </div>
                <div className="absolute -right-10 -bottom-10 opacity-10">
                  <span className="material-symbols-outlined text-[12rem]">settings_input_component</span>
                </div>
              </div>
              {/* Small Feature */}
              <div className="bg-surface-container-low p-8 rounded-2xl border border-outline-variant/20">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                  <span className="material-symbols-outlined">edit_note</span>
                </div>
                <h3 className="font-headline text-xl font-bold mb-3">Live Form Builder</h3>
                <p className="text-on-surface-variant text-sm">Customize your feedback form without shipping a new update. Add fields, stars, or multiple choice on the fly.</p>
              </div>
              {/* Medium Feature */}
              <div className="bg-surface-container-low p-8 rounded-2xl border border-outline-variant/20 md:col-span-2 flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-1">
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary mb-6">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                  </div>
                  <h3 className="font-headline text-xl font-bold mb-3">Star ratings &amp; screenshots</h3>
                  <p className="text-on-surface-variant text-sm">Users can highlight specific areas of the screen. We handle the image processing and storage so you don't have to.</p>
                </div>
                <div className="flex-1 w-full">
                  <div className="bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30 flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-secondary-fixed"></div>
                    <div className="space-y-1 flex-1">
                      <div className="h-2 w-1/2 bg-surface-container-highest rounded"></div>
                      <div className="h-2 w-full bg-surface-container-highest rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Small Feature */}
              <div className="bg-white p-8 rounded-2xl border border-outline-variant/20 card-shadow">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                  <span className="material-symbols-outlined">folder_shared</span>
                </div>
                <h3 className="font-headline text-xl font-bold mb-3">Multi-project support</h3>
                <p className="text-on-surface-variant text-sm">Manage feedback for all your apps in one central hub. Switch between projects seamlessly as you grow your portfolio.</p>
              </div>
              {/* Small Feature */}
              <div className="bg-white p-8 rounded-2xl border border-outline-variant/20 card-shadow">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary mb-6">
                  <span className="material-symbols-outlined">sell</span>
                </div>
                <h3 className="font-headline text-xl font-bold mb-3">Tag &amp; filter feedback</h3>
                <p className="text-on-surface-variant text-sm">Organize feedback with custom tags. Create smart views for "Bugs", "Feature Requests", or "Testimonials".</p>
              </div>
              {/* Small Feature */}
              <div className="bg-surface-container-highest p-8 rounded-2xl flex flex-col justify-center items-center text-center">
                <span className="material-symbols-outlined text-4xl text-primary mb-4">terminal</span>
                <p className="font-headline font-bold">100% Type-Safe</p>
                <p className="text-xs text-on-surface-variant mt-2">First-class TypeScript support for all SDK methods.</p>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section className="py-24 bg-surface" id="pricing">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="font-headline text-3xl lg:text-5xl font-extrabold tracking-tight text-on-surface mb-4">Simple, scale-with-you pricing.</h2>
                <p className="text-on-surface-variant max-w-2xl mx-auto">Start for free and upgrade as your indie app gains traction.</p>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {/* Free Plan */}
                <div className="bg-surface-container-lowest p-8 rounded-2xl border border-outline-variant/20 flex flex-col">
                  <h3 className="font-headline text-xl font-bold mb-2">Free</h3>
                  <div className="mb-6 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold tracking-tight">$0</span>
                    <span className="text-on-surface-variant">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10 flex-1">
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 500 subs</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 3 projects</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 100MB storage</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> Email support</li>
                  </ul>
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-3 rounded-lg border-2 border-outline-variant/30 font-bold hover:bg-surface-container-low transition-colors"
                  >
                    Get Started
                  </button>
                </div>
                {/* Indie Plan */}
                <div className="bg-surface-container-lowest p-8 rounded-2xl border-2 border-primary card-shadow relative flex flex-col transform md:-translate-y-4">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-on-primary text-[0.65rem] uppercase tracking-widest font-black px-4 py-1 rounded-full">Most Popular</div>
                  <h3 className="font-headline text-xl font-bold mb-2 text-primary">Indie</h3>
                  <div className="mb-6 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold tracking-tight">$9</span>
                    <span className="text-on-surface-variant">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10 flex-1">
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 5,000 subs</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 10 projects</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 1GB storage</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> Priority email</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 7-day history</li>
                  </ul>
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-3 rounded-lg hero-gradient text-on-primary font-bold shadow-lg"
                  >
                    Start 14-Day Trial
                  </button>
                </div>
                {/* Studio Plan */}
                <div className="bg-surface-container-lowest p-8 rounded-2xl border border-outline-variant/20 flex flex-col">
                  <h3 className="font-headline text-xl font-bold mb-2">Studio</h3>
                  <div className="mb-6 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold tracking-tight">$29</span>
                    <span className="text-on-surface-variant">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10 flex-1">
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 50,000 subs</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> Unlimited projects</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 10GB storage</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> Slack support</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 30-day history</li>
                    <li className="flex gap-3 text-sm items-center"><span className="material-symbols-outlined text-secondary text-sm">check_circle</span> 5 team seats</li>
                  </ul>
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-3 rounded-lg border-2 border-outline-variant/30 font-bold hover:bg-surface-container-low transition-colors"
                  >
                    Go Studio
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="max-w-7xl mx-auto px-6 mb-24">
            <div className="hero-gradient rounded-3xl p-12 lg:p-20 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                <svg height="100%" width="100%" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern height="40" id="grid" patternUnits="userSpaceOnUse" width="40">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"></path>
                    </pattern>
                  </defs>
                  <rect fill="url(#grid)" height="100%" width="100%"></rect>
                </svg>
              </div>
              <div className="relative z-10">
                <h2 className="font-headline text-4xl lg:text-6xl font-extrabold text-on-primary tracking-tighter mb-8">Ready to streamline your user feedback?</h2>
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <button
                    onClick={() => navigate('/register')}
                    className="bg-secondary-fixed text-on-secondary-fixed font-headline font-bold px-10 py-5 rounded-xl hover:scale-105 transition-transform shadow-xl"
                  >
                    Sign Up for Free
                  </button>
                  <div className="flex items-center gap-2 text-on-primary-container font-medium">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>groups</span>
                    340+ members on Discord
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-slate-50 border-t border-slate-200/20 py-12">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <span className="text-lg font-black text-blue-900 block mb-4">FeedbackKit</span>
              <p className="text-xs font-inter uppercase tracking-widest text-slate-500">© 2024 FeedbackKit. Built for precision.</p>
            </div>
            <div>
              <h4 className="font-inter text-xs uppercase tracking-widest text-blue-900 font-bold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#features">Features</a></li>
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#pricing">Pricing</a></li>
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#">Docs</a></li>
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#">Status</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-inter text-xs uppercase tracking-widest text-blue-900 font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#">Privacy Policy</a></li>
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-inter text-xs uppercase tracking-widest text-blue-900 font-bold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#">Twitter</a></li>
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#">GitHub</a></li>
                <li><a className="text-slate-500 text-xs hover:underline decoration-teal-500 underline-offset-4 transition-opacity" href="#">Discord</a></li>
              </ul>
            </div>
          </div>
        </footer>
    </div>
  )
}
